import React from 'react'
import styled from '@emotion/styled'
import { ReverCard, ReverLoading } from '@itsrever/design-system'
import ReturnsIcon from '@mui/icons-material/LocalShipping'
import RDVIcon from '@mui/icons-material/AccountBalance'
import SalesIcon from '@mui/icons-material/Paid'
import ArrowUpIcon from '@mui/icons-material/TrendingUp'
import { moreThan } from '@/utils'
import { formatPercent } from '@/utils/format'
import DonutComponent from '../donut/donut'
import { useTranslation } from 'react-i18next'
import LineChartComponent from '../line-chart-component/line-chart-component'
import type { Moment } from 'moment'
import {
  useReturnMetrics,
  useDailyReturns,
  useCountriesReturns
} from '@/application/queries'
import { CompensationsChart } from './compensations-chart'
import { NoData } from '@/ui/shared'

export type CalendarDate = {
  fromDate: Moment | null
  toDate: Moment | null
}

interface ReturnsMetricsProps {
  calendarDate: CalendarDate
}

const ReturnsMetrics: React.FC<ReturnsMetricsProps> = ({ calendarDate }) => {
  const { t, i18n } = useTranslation()

  const startDate = calendarDate.fromDate?.format('YYYY-MM-DD')
  const endDate = calendarDate.toDate?.format('YYYY-MM-DD')

  const { returnMetrics, loadingReturnMetrics } = useReturnMetrics(
    startDate,
    endDate
  )

  const {
    returnDays,
    totalItemsReturnedByDay,
    dailyReturns,
    loadingDailyReturns
  } = useDailyReturns(startDate, endDate)

  const {
    countryList,
    countriesTotalReturns,
    countriesPercentage,
    totalReturns,
    loadingCountriesReturns
  } = useCountriesReturns(startDate, endDate)

  if (loadingReturnMetrics || loadingDailyReturns || loadingCountriesReturns)
    return <ReverLoading />
  if (!returnMetrics || !dailyReturns) return <NoData />

  return (
    <ReturnsDiv>
      <TopInfo>
        <ReverCard className="p-4" fullWidth>
          <div className="flex items-center gap-2">
            <div className="text-[#335CFF]">
              <ReturnsIcon />
            </div>
            <p className="text-sm text-[#0E121B]">
              {t('returns_analytics.title_returns')}
            </p>
          </div>
          <h3 className="mt-4 font-semibold">
            {new Intl.NumberFormat(i18n.language).format(
              returnMetrics.totalReturns
            )}
          </h3>
        </ReverCard>
        <ReverCard className="p-4" fullWidth>
          <div className="flex items-center gap-2">
            <div className="text-[#335CFF]">
              <RDVIcon />
            </div>
            <p className="text-sm text-[#0E121B]">
              {t('returns_analytics.title_rdv')}
            </p>
          </div>
          <h3 className="mt-4 font-semibold">
            {formatPercent(returnMetrics.rdvPercentage, i18n.language)}
          </h3>
        </ReverCard>
        <ReverCard className="p-4" fullWidth>
          <div className="flex items-center gap-2">
            <div className="text-[#335CFF]">
              <SalesIcon />
            </div>
            <p className="text-sm text-[#0E121B]">
              {t('returns_analytics.title_sales')}
            </p>
          </div>
          <h3 className="mt-4 flex items-center gap-2 font-semibold">
            {returnMetrics.rdvAmount}
            <div className="mr-2 text-[#1FC16B]">
              <ArrowUpIcon />
            </div>
          </h3>
        </ReverCard>
      </TopInfo>
      <LineChartComponent
        title={t('returns_analytics.title_returned_items')}
        subtitle={t('returns_analytics.chart_subtitle')}
        values={totalItemsReturnedByDay}
        days={returnDays}
      />
      <CompensationsDiv>
        <ReverCard className="flex items-center justify-around p-4" fullWidth>
          <div className="size-full">
            <DonutComponent
              title={t('returns_analytics.title_countries')}
              labels={countryList}
              values={countriesPercentage}
              amounts={countriesTotalReturns}
              totalAmount={totalReturns}
            />
          </div>
        </ReverCard>
        <ReverCard className="flex items-center p-4" fullWidth>
          <CompensationsChart
            compensationAmounts={returnMetrics.compensationAmounts}
            totalCompensation={returnMetrics.totalCompensation}
          />
        </ReverCard>
      </CompensationsDiv>
    </ReturnsDiv>
  )
}

export default ReturnsMetrics

const ReturnsDiv = styled.div`
  height: 100%;
  width: 100%;
`

const TopInfo = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media ${moreThan.md} {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
  }
`

const CompensationsDiv = styled.div`
  margin-top: 1rem;
  display: flex;
  padding-bottom: 4rem;
  gap: 1rem;
`
